import { useCallback, useMemo, useRef, useState } from 'react';
import { RowGroup, ScorableItem } from '../NodeListItemGroup';
import useScorableItemSelection from './useScorableItemSelection';
import { ListCreator } from './useScorableItemFilters.d';
import { compose } from './utils';
import { DateFieldProps } from '@pelckmans/elementis/components/date-field';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';

export type ScorableItemFilter = (item: ScorableItem) => boolean;

function countScorableItemsInTree(node: RowGroup, countMap: Map<string, number>, filter?: ScorableItemFilter) {
  const filteredScorableItemsCount = filter
    ? node.scorableItems?.filter(filter).length || 0
    : node.scorableItems?.length || 0;

  const childNodesScorableItemsCount =
    node.nodes?.reduce((acc, childNode): number => {
      if (countMap.has(childNode.id)) {
        return acc + countMap.get(childNode.id)!;
      }

      const countForChildNode = countScorableItemsInTree(childNode, countMap, filter);
      countMap.set(childNode.id, countForChildNode);
      return countForChildNode + acc;
    }, 0) || 0;

  const scorableItemsCountForNode = filteredScorableItemsCount + childNodesScorableItemsCount;

  countMap.set(node.id, scorableItemsCountForNode);
  return scorableItemsCountForNode;
}

function nodeTitleMatchesTitleFilter(
  node: RowGroup,
  nodeTitleFulfillmentMap: Map<string, boolean>,
  titleFilter: (obj: { title: string }) => boolean,
): boolean {
  if (titleFilter(node)) {
    nodeTitleFulfillmentMap.set(node.id, true);
    return true;
  }

  const childNodesConform =
    node.nodes?.some(childNode => nodeTitleMatchesTitleFilter(childNode, nodeTitleFulfillmentMap, titleFilter)) ||
    false;

  nodeTitleFulfillmentMap.set(node.id, childNodesConform);
  return childNodesConform;
}

export const resultTypes = ['all', 'free-exercise', 'assignment', 'none'] as const;
export type ResultType = typeof resultTypes[number];

interface Data {
  nodeRowGroups: RowGroup[];
}

function isRowGroup(rowGroup: RowGroup | null): rowGroup is RowGroup {
  return rowGroup !== null;
}

function isListCreator(creator: ListCreator | undefined): creator is ListCreator {
  return creator !== undefined;
}

function listWithTypeFilterCreatorFactory(countMap: Map<string, number>, typeFilter: ScorableItemFilter): ListCreator {
  countMap.clear();

  function mapNode(node: RowGroup): RowGroup | null {
    const subTreeScorableItemCount = countMap.has(node.id)
      ? countMap.get(node.id)!
      : countScorableItemsInTree(node, countMap, typeFilter);

    if (subTreeScorableItemCount === 0) return null;

    return {
      ...node,
      scorableItems: node.scorableItems?.filter(typeFilter),
      nodes: node.nodes?.map(childNode => mapNode(childNode)).filter(isRowGroup),
    };
  }

  return rootNodes => {
    return rootNodes.map(mapNode).filter(isRowGroup);
  };
}

function listWithTitleFilterCreatorFactory(
  countMap: Map<string, number>,
  titleNodeMap: Map<string, boolean>,
  titleFilter: (obj: { title: string }) => boolean,
): ListCreator {
  countMap.clear();
  titleNodeMap.clear();

  function mapNode(node: RowGroup): RowGroup | null {
    if (titleFilter(node)) return node;

    const subTreeScorableItemCount = countMap.has(node.id)
      ? countMap.get(node.id)!
      : countScorableItemsInTree(node, countMap, titleFilter);

    const conformToTitleFilter = nodeTitleMatchesTitleFilter(node, titleNodeMap, titleFilter);

    if (subTreeScorableItemCount === 0 && !conformToTitleFilter) return null;

    return {
      ...node,
      scorableItems: node.scorableItems?.filter(titleFilter),
      nodes: node.nodes?.map(childNode => mapNode(childNode)).filter(isRowGroup),
    };
  }

  return rootNodes => {
    return rootNodes.map(mapNode).filter(isRowGroup);
  };
}

function listWithDateRangeFilterCreatorFactory(
  countMap: Map<string, number>,
  dateFilter: (obj: Extract<ScorableItem, { scorableItemType: 'assignment' }>) => boolean,
  allowedScorableItemType: ScorableItemTypeFilterConfig['value'],
): ListCreator | undefined {
  countMap.clear();

  function mapNode(node: RowGroup): RowGroup | null {
    const subTreeScorableItemCount = countMap.has(node.id)
      ? countMap.get(node.id)!
      : countScorableItemsInTree(node, countMap, item => {
          // free-exercise do not need to match the date filter
          if (item.scorableItemType === 'free-exercise') {
            return allowedScorableItemType === 'all';
          }

          return dateFilter(item);
        });

    if (subTreeScorableItemCount === 0) return null;

    return {
      ...node,
      scorableItems: node.scorableItems?.filter(item => {
        // free-exercise do not need to match the date filter
        if (item.scorableItemType === 'free-exercise') {
          return allowedScorableItemType === 'all';
        }

        return dateFilter(item);
      }),
      nodes: node.nodes?.map(childNode => mapNode(childNode)).filter(isRowGroup),
    };
  }

  return rootNodes => {
    return rootNodes.map(mapNode).filter(isRowGroup);
  };
}

type ScorableItemTypeFilterConfig =
  | {
      value: Extract<ResultType, 'all'>;
      listCreator: undefined;
    }
  | {
      value: Extract<ResultType, 'free-exercise' | 'assignment' | 'none'>;
      listCreator: ListCreator;
    };

type TitleFilterConfig = {
  value: string;
  listCreator: ListCreator | undefined;
};

type DateFilterConfig = {
  value: Extract<DateFieldProps, { mode: 'range' }>['selected'];
  listCreator: ListCreator | undefined;
};

export default function useScorableItemFilters({ nodeRowGroups }: Data) {
  const [scorableItemTypeFilterConfig, setScorableItemTypeFilterConfig] = useState<ScorableItemTypeFilterConfig>({
    value: 'all',
    listCreator: undefined,
  });

  const [titleFilterConfig, setTitleFilterConfig] = useState<TitleFilterConfig>({
    value: '',
    listCreator: undefined,
  });

  const [dateFilterConfig, setDateFilterConfig] = useState<DateFilterConfig>({
    value: undefined,
    listCreator: undefined,
  });

  const [checkedScorableItemListCreator, setCheckedScorableItemListCreator] = useState<ListCreator | undefined>(
    undefined,
  );

  /**
   * These refs are used as optimizations for traversing the node tree
   */
  // map with count of scorable items per node, and its sub-nodes which comply with the type filter
  const scorableItemWithTypeFilterCountPerNode = useRef<Map<string, number>>(new Map());
  // map with count of scorable items per node, and its sub-nodes which comply with the title filter
  const scorableItemWithTitleFilterCountPerNode = useRef<Map<string, number>>(new Map());
  // map with boolean value indicating whether the node or any of its sub-nodes comply with the title filter
  const nodeOrSubNodesTitleFulfillmentPerNode = useRef<Map<string, boolean>>(new Map());
  // map with count of scorable items per node, and its sub-nodes, which comply to the date filter
  const scorableItemsWithDateFilterCountPerNode = useRef<Map<string, number>>(new Map());

  const changeDateFilters = useCallback(
    (value: DateFilterConfig['value'], allowedScorableItemType: ScorableItemTypeFilterConfig['value']) => {
      if (!value || !value.from || !value.to) {
        setDateFilterConfig({
          value,
          listCreator: undefined,
        });
        return;
      }

      const listCreator: ListCreator | undefined =
        allowedScorableItemType !== 'free-exercise'
          ? listWithDateRangeFilterCreatorFactory(
              scorableItemsWithDateFilterCountPerNode.current,
              item => {
                return (
                  startOfDay(item.assignmentStartDate).getTime() >= startOfDay(value.from!).getTime() &&
                  endOfDay(item.assignmentEndDate).getTime() <= endOfDay(value.to!).getTime()
                );
              },
              allowedScorableItemType,
            )
          : undefined;

      const newValue = allowedScorableItemType === 'free-exercise' ? undefined : value;

      setDateFilterConfig({
        value: newValue,
        listCreator,
      });
    },
    [],
  );

  const changeScorableItemType = useCallback(
    (type: ResultType) => {
      if (type === scorableItemTypeFilterConfig.value) return;

      if (type === 'all') {
        setScorableItemTypeFilterConfig({ value: type, listCreator: undefined });
      } else if (type === 'none') {
        setScorableItemTypeFilterConfig({
          value: type,
          listCreator: (): RowGroup[] => [],
        });
      } else {
        const listCreator = listWithTypeFilterCreatorFactory(
          scorableItemWithTypeFilterCountPerNode.current,
          item => item.scorableItemType === type,
        );

        setScorableItemTypeFilterConfig({
          value: type,
          listCreator,
        });
      }

      changeDateFilters(dateFilterConfig.value, type);
    },
    [changeDateFilters, dateFilterConfig.value, scorableItemTypeFilterConfig.value],
  );

  const changeTitleFilterText = useCallback((filterText: string) => {
    setTitleFilterConfig(prev => ({
      ...prev,
      value: filterText,
    }));
  }, []);

  const changeTitleFilters = useCallback((value: string) => {
    let listCreator: ListCreator | undefined;
    if (value) {
      listCreator = listWithTitleFilterCreatorFactory(
        scorableItemWithTitleFilterCountPerNode.current,
        nodeOrSubNodesTitleFulfillmentPerNode.current,
        ({ title }) => title.toLowerCase().includes(value.toLowerCase()),
      );
    }

    setTitleFilterConfig(prev => ({
      ...prev,
      listCreator,
    }));
  }, []);

  /**
   * this represents the list items which need to be rendered in the modal
   * its result is a combination of the scorable item type filter and the title filter
   * each filter has the responsibility to create a list of items that match its criteria
   */
  const listItems = useMemo(() => {
    const creators = [
      scorableItemTypeFilterConfig.listCreator,
      titleFilterConfig.listCreator,
      dateFilterConfig.listCreator,
    ].filter(isListCreator);

    if (
      checkedScorableItemListCreator &&
      (scorableItemTypeFilterConfig.listCreator || titleFilterConfig.listCreator || dateFilterConfig.listCreator)
    ) {
      creators.push(checkedScorableItemListCreator);
    }

    if (creators.length === 0) return nodeRowGroups;

    const creator = compose(...creators);

    return creator(nodeRowGroups);
  }, [
    checkedScorableItemListCreator,
    dateFilterConfig.listCreator,
    nodeRowGroups,
    scorableItemTypeFilterConfig.listCreator,
    titleFilterConfig.listCreator,
  ]);

  const clearFilters = useCallback(() => {
    changeTitleFilterText('');
    changeTitleFilters('');
    changeScorableItemType('all');
    changeDateFilters(undefined, 'all');
  }, [changeDateFilters, changeScorableItemType, changeTitleFilterText, changeTitleFilters]);

  const selection = useScorableItemSelection({
    filteredNodeTrees: listItems,
    allNodeTrees: nodeRowGroups,
    setListCreator: setCheckedScorableItemListCreator,
  });

  let filterActive = false;
  if (titleFilterConfig.listCreator !== undefined) filterActive = true;
  else if (scorableItemTypeFilterConfig.value !== 'all') filterActive = true;
  else if (scorableItemTypeFilterConfig.value === 'all' && dateFilterConfig.value) filterActive = true;

  return {
    filterActive,
    scorableItemType: scorableItemTypeFilterConfig.value,
    titleFilterText: titleFilterConfig.value,
    dateRange: dateFilterConfig.value,
    setTitleFilterText: changeTitleFilterText,
    setTitleFilters: changeTitleFilters,
    setScorableItemType: changeScorableItemType,
    setDateRange: changeDateFilters,
    clearFilters,
    listItems,
    ...selection,
  };
}
